import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, TableBody, TableCell, TableHead, TableRow, Alert, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress
} from '@mui/material';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import { formatAmount } from '../../utils/money';
import ScrollTable from '../ScrollTable';

// Grouped without a currency prefix — these tables carry the currency in their heading.
const fmt = (n) => formatAmount(n);

const asDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const YearEndClose = () => {
  const [fiscalYear, setFiscalYear] = useState(null);
  const [closes, setCloses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [fy, list] = await Promise.all([
        axios.get('/accounting/fiscal-year'),
        axios.get('/accounting/period-closes')
      ]);
      setFiscalYear(fy.data);
      setCloses(list.data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load year-end information');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Closing the year that is still running would shut its remaining months out of their own
  // year, so the action offers the most recent year that has actually finished.
  const closeYear = async (within) => {
    try {
      setWorking(true);
      const res = await axios.post('/accounting/period-closes', { within });
      toast.success(res.data.message || 'Year closed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to close the year');
    } finally {
      setWorking(false);
    }
  };

  const reopen = async () => {
    try {
      setWorking(true);
      await axios.post(`/accounting/period-closes/${reopenTarget._id}/reopen`, { reason });
      toast.success(`${reopenTarget.label} reopened`);
      setReopenTarget(null);
      setReason('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to reopen the year');
    } finally {
      setWorking(false);
    }
  };

  if (loading && !fiscalYear) return <CircularProgress size={24} />;

  const priorYearDate = fiscalYear
    ? new Date(new Date(fiscalYear.start).getTime() - 86400000).toISOString().slice(0, 10)
    : null;

  return (
    <Box>
      {fiscalYear && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Current financial year: {fiscalYear.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {asDate(fiscalYear.start)} to {asDate(fiscalYear.end)} · year end {fiscalYear.yearEndLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            The year end is set under Company Information. Changing it moves every period
            boundary, so it should be settled before any year is closed.
          </Typography>
        </Paper>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Closing a year moves its profit or loss into Retained Earnings and stops anything
        further being posted into it. Nothing is hidden — closed years stay fully reportable.
        A year can only be closed once it has ended.
      </Alert>

      <Box mb={3} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="contained" disabled={working || !priorYearDate}
          onClick={() => closeYear(priorYearDate)}
        >
          Close the year ending {priorYearDate ? asDate(priorYearDate) : ''}
        </Button>
      </Box>

      <Typography variant="subtitle1" fontWeight={700} gutterBottom>Closed years</Typography>
      <ScrollTable containerProps={{ component: Paper, variant: 'outlined', sx: { overflowX: 'auto' } }} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Year</TableCell>
              <TableCell>Ended</TableCell>
              <TableCell align="right">Income</TableCell>
              <TableCell align="right">Expenses</TableCell>
              <TableCell align="right">Result</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {closes.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ color: 'text.secondary' }}>
                  No year has been closed yet.
                </TableCell>
              </TableRow>
            )}
            {closes.map((c) => (
              <TableRow key={c._id}>
                <TableCell>{c.label}</TableCell>
                <TableCell>{asDate(c.fiscalYearEnd)}</TableCell>
                <TableCell align="right">{fmt(c.totalIncome)}</TableCell>
                <TableCell align="right">{fmt(c.totalExpense)}</TableCell>
                <TableCell align="right" sx={{ color: Number(c.netResult) < 0 ? 'error.main' : 'success.main' }}>
                  {fmt(c.netResult)}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={c.status === 'closed' ? 'Closed' : 'Reopened'}
                    color={c.status === 'closed' ? 'default' : 'warning'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  {c.status === 'closed' && (
                    <Button size="small" onClick={() => setReopenTarget(c)}>Reopen</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ScrollTable>

      <Dialog open={!!reopenTarget} onClose={() => setReopenTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reopen {reopenTarget?.label}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This reverses the closing entry so the year's income and expenses return to their
            own accounts. The original entry and its reversal both stay in the ledger. Close
            the year again once the correction is made.
          </Alert>
          <TextField
            autoFocus fullWidth multiline minRows={2} label="Reason"
            value={reason} onChange={(e) => setReason(e.target.value)}
            helperText="Recorded against the reopening. Required."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenTarget(null)}>Cancel</Button>
          <Button
            variant="contained" color="warning"
            disabled={working || !reason.trim()} onClick={reopen}
          >
            Reopen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default YearEndClose;
